/**
 * Agency name to badge label/color mapping for government agency badges.
 *
 * Maps common federal agency names (and sub-agencies) to short labels
 * and Tailwind color classes for display in product cards and overlays.
 */

export interface AgencyBadgeConfig {
  label: string
  className: string
}

/**
 * Mapping of agency name substrings to badge display configs.
 * Order matters -- first match wins.
 */
const AGENCY_MAPPINGS: Array<{ pattern: string; config: AgencyBadgeConfig }> = [
  // Military branches (check before parent DoD)
  { pattern: 'Army', config: { label: 'Army', className: 'bg-green-700 text-white dark:bg-green-700 dark:text-white' } },
  { pattern: 'Navy', config: { label: 'Navy', className: 'bg-blue-800 text-white dark:bg-blue-700 dark:text-white' } },
  { pattern: 'Air Force', config: { label: 'USAF', className: 'bg-sky-700 text-white dark:bg-sky-600 dark:text-white' } },
  { pattern: 'Marine', config: { label: 'USMC', className: 'bg-red-700 text-white dark:bg-red-700 dark:text-white' } },
  { pattern: 'Space Force', config: { label: 'USSF', className: 'bg-indigo-700 text-white dark:bg-indigo-600 dark:text-white' } },
  { pattern: 'Coast Guard', config: { label: 'USCG', className: 'bg-orange-600 text-white dark:bg-orange-600 dark:text-white' } },
  // DoD-wide
  { pattern: 'Defense Logistics', config: { label: 'DLA', className: 'bg-blue-700 text-white dark:bg-blue-600 dark:text-white' } },
  { pattern: 'DARPA', config: { label: 'DARPA', className: 'bg-violet-700 text-white dark:bg-violet-600 dark:text-white' } },
  { pattern: 'Defense Advanced', config: { label: 'DARPA', className: 'bg-violet-700 text-white dark:bg-violet-600 dark:text-white' } },
  { pattern: 'Missile Defense', config: { label: 'MDA', className: 'bg-blue-700 text-white dark:bg-blue-600 dark:text-white' } },
  { pattern: 'Department of Defense', config: { label: 'DoW', className: 'bg-blue-800 text-white dark:bg-blue-700 dark:text-white' } },
  { pattern: 'DOD', config: { label: 'DoW', className: 'bg-blue-800 text-white dark:bg-blue-700 dark:text-white' } },
  // Civilian agencies
  { pattern: 'Homeland Security', config: { label: 'DHS', className: 'bg-teal-700 text-white dark:bg-teal-600 dark:text-white' } },
  { pattern: 'Customs and Border', config: { label: 'CBP', className: 'bg-teal-700 text-white dark:bg-teal-600 dark:text-white' } },
  { pattern: 'Energy', config: { label: 'DOE', className: 'bg-amber-600 text-white dark:bg-amber-600 dark:text-white' } },
  { pattern: 'NASA', config: { label: 'NASA', className: 'bg-red-700 text-white dark:bg-red-600 dark:text-white' } },
  { pattern: 'Aeronautics', config: { label: 'NASA', className: 'bg-red-700 text-white dark:bg-red-600 dark:text-white' } },
  { pattern: 'Intelligence', config: { label: 'IC', className: 'bg-slate-600 text-white dark:bg-slate-600 dark:text-white' } },
  { pattern: 'Veterans Affairs', config: { label: 'VA', className: 'bg-purple-700 text-white dark:bg-purple-600 dark:text-white' } },
  { pattern: 'Health and Human', config: { label: 'HHS', className: 'bg-pink-700 text-white dark:bg-pink-600 dark:text-white' } },
  { pattern: 'GSA', config: { label: 'GSA', className: 'bg-cyan-700 text-white dark:bg-cyan-600 dark:text-white' } },
  { pattern: 'General Services', config: { label: 'GSA', className: 'bg-cyan-700 text-white dark:bg-cyan-600 dark:text-white' } },
  { pattern: 'State', config: { label: 'State', className: 'bg-slate-700 text-white dark:bg-slate-600 dark:text-white' } },
  { pattern: 'Transportation', config: { label: 'DOT', className: 'bg-orange-700 text-white dark:bg-orange-600 dark:text-white' } },
  { pattern: 'Justice', config: { label: 'DOJ', className: 'bg-yellow-600 text-white dark:bg-yellow-600 dark:text-white' } },
  { pattern: 'Interior', config: { label: 'DOI', className: 'bg-emerald-700 text-white dark:bg-emerald-600 dark:text-white' } },
  { pattern: 'Commerce', config: { label: 'DOC', className: 'bg-blue-600 text-white dark:bg-blue-600 dark:text-white' } },
  { pattern: 'Treasury', config: { label: 'USDT', className: 'bg-green-700 text-white dark:bg-green-600 dark:text-white' } },
  { pattern: 'Agriculture', config: { label: 'USDA', className: 'bg-lime-700 text-white dark:bg-lime-600 dark:text-white' } },
]

const DEFAULT_CONFIG: AgencyBadgeConfig = {
  label: '',
  className: 'bg-slate-600 text-white dark:bg-slate-600 dark:text-white',
}

/**
 * Get badge configuration for an agency name. First match wins.
 * Falls back to a generic badge with a truncated label.
 */
export function getAgencyBadge(agencyName: string, subAgencyName?: string): AgencyBadgeConfig {
  const searchStr = `${agencyName || ''} ${subAgencyName || ''}`
  for (const { pattern, config } of AGENCY_MAPPINGS) {
    if (searchStr.includes(pattern)) {
      return config
    }
  }
  // Fallback: abbreviate long names
  const label = abbreviateAgency(agencyName || 'Gov')
  return { ...DEFAULT_CONFIG, label }
}

function abbreviateAgency(name: string): string {
  if (name.length <= 6) return name
  // Try "Department of X" -> extract X
  const deptMatch = name.match(/Department of (?:the )?(\w+)/)
  if (deptMatch) return deptMatch[1].slice(0, 5)
  // Take initials of first few words
  return name
    .split(/\s+/)
    .slice(0, 3)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')
}

/**
 * Deduplicate agency relationships for badge display.
 * Returns unique agency badges (by label).
 */
export function getUniqueAgencyBadges(
  relationships: Array<{ agency_name: string; sub_agency_name: string }>
): AgencyBadgeConfig[] {
  const seen = new Set<string>()
  const badges: AgencyBadgeConfig[] = []
  for (const rel of relationships) {
    const badge = getAgencyBadge(rel.agency_name, rel.sub_agency_name)
    if (!seen.has(badge.label)) {
      seen.add(badge.label)
      badges.push(badge)
    }
  }
  return badges
}
